import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';

export const useMessaging = ({ caseId, apiClasses }) => {
  const [patientMessages, setPatientMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPatientMessageList = useCallback(async () => {
    if (!caseId || !apiClasses?.ChatRoomAPI) {
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const response = await apiClasses.ChatRoomAPI.getChatRoomMessages(caseId);
      const { data } = response;
      if (!data?.success) {
        throw new Error("Error fetching patient messages");
      }
      const messages = data.payload.messages || [];
      setPatientMessages(messages);
    } catch (err) {
      setError(err.message || 'Failed to fetch patient messages');
      message.error('Error fetching patient messages');
    } finally {
      setLoading(false);
    }
  }, [caseId, apiClasses?.ChatRoomAPI]);

  const sendPatientMessage = useCallback(async (messageText) => {
    if (!caseId || !apiClasses?.ChatRoomAPI) {
      return false;
    }

    const payload = {
      chatRoomId: caseId,
      message: messageText,
    };

    try {
      const { data } = await apiClasses.ChatRoomAPI.addMessagesToChatRoom(payload);
      if (!data.success) {
        throw new Error('Failed to send message');
      }
      setPatientMessages(list => list.concat(data.payload));
      return true;
    } catch (err) {
      message.error('Error sending message to patient');
      return false;
    }
  }, [caseId, apiClasses?.ChatRoomAPI]);

  const markPatientChatAsRead = useCallback(async () => {
    if (!caseId || !apiClasses?.ChatRoomAPI) {
      return;
    }

    try {
      const { data } = await apiClasses.ChatRoomAPI.markChatAsRead(caseId);
      if (!data.success) {
        throw new Error('Error marking patient chat as read');
      }

      setPatientMessages(state => {
        return state.map(message => {
          if (message.isPatient && !message.read) {
            return { ...message, read: true };
          }
          return message;
        });
      });
    } catch (err) {
      message.error('Error sending message to patient');
      return false;
    }
  }, [caseId, apiClasses?.ChatRoomAPI]);

  useEffect(() => {
    fetchPatientMessageList();
  }, [fetchPatientMessageList]);

  return {
    patientMessages,
    loading,
    error,
    sendPatientMessage,
    markPatientChatAsRead,
    fetchPatientMessageList
  };
};
